import React, { useState, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import { Box, IconButton, Typography, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleXmark,
  faUpload,
  faTrash,
  faCheckCircle,
  faInfoCircle,
  faMoneyBillWave,
  faPen,
  faEraser,
  faEye,
  faClose,
  faShare,
  faCamera,
} from "@fortawesome/free-solid-svg-icons";
import { notification } from "antd";
import moment from "moment";
import { statusMessage } from "../../../controllers/common";
import "../winningVouchers.css";

import {payoutVoucher} from "../../../controllers/V1/winnersControllers"
import { useNavigate } from "react-router-dom";
import {compressImageToJpeg} from "../../../controllers/compressImage";

const PayoutPopup = ({ popUpOpen, togglePopUp, voucherData }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImageSrc, setViewerImageSrc] = useState("");
  const [viewerImageTitle, setViewerImageTitle] = useState("");
  const [imageLoading, setImageLoading] = useState(false);

  const handleCancel = () => {
    if (!isSubmitting) {
      togglePopUp();
      setUploadedFiles([]);
      clearSignature();
    }
  };

  // Handle ESC key to close image viewer
  React.useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && imageViewerOpen) {
        closeImageViewer();
      }
    };

    if (imageViewerOpen) {
      window.addEventListener("keydown", handleEscKey);
      return () => {
        window.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [imageViewerOpen]);

  // Initialize canvas
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !popUpOpen) return;

    // Set canvas size based on display size
    const setCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const displayHeight = isMobile ? 200 : isTablet ? 250 : 300;
      
      canvas.width = rect.width * dpr;
      canvas.height = displayHeight * dpr;
      
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = isMobile ? 2 : 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      setCanvasSize();
    }, 100);

    // Handle resize
    const handleResize = () => {
      setCanvasSize();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [popUpOpen, isMobile, isTablet]);

  // Handle canvas drawing
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    
    // Get touch or mouse coordinates relative to canvas
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      // Handle touchEnd event
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Calculate position relative to canvas bounding rect
    // Since context is already scaled by dpr, use coordinates directly
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    return { x, y };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    if (isDrawing) {
      setIsDrawing(false);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) {
      notification.open({
        message: "Warning",
        description: "Please provide your signature before saving.",
        placement: "topRight",
        icon: <FontAwesomeIcon style={{ color: "#f59e0b" }} icon={faInfoCircle} />,
        duration: 2,
      });
      return;
    }

    canvas.toBlob(
      (blob) => {
        const fileName = `signature_${Date.now()}.png`;
        const file = new File([blob], fileName, { type: "image/png" });

        const signatureFile = {
          id: Date.now(),
          file: file,
          name: fileName,
          size: file.size,
          type: "image/png",
          preview: URL.createObjectURL(blob),
          isSignature: true,
        };

        // Remove existing signature if any
        setUploadedFiles((prev) => {
          const existingSignature = prev.find((f) => f.isSignature);
          if (existingSignature?.preview) {
            URL.revokeObjectURL(existingSignature.preview);
          }
          const filtered = prev.filter((f) => !f.isSignature);
          return [...filtered, signatureFile];
        });

        notification.open({
          message: "Success",
          description: "Signature saved successfully!",
          placement: "topRight",
          icon: <FontAwesomeIcon style={{ color: "green" }} icon={faCheckCircle} />,
          duration: 2,
        });
      },
      "image/png",
      1.0
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      notification.open({
        message: "Warning",
        description: "Please select only image files.",
        placement: "topRight",
        icon: <FontAwesomeIcon style={{ color: "#f59e0b" }} icon={faInfoCircle} />,
        duration: 2,
      });
      return;
    }

    // Check if already have 2 images
    setUploadedFiles((prev) => {
      const existingImages = prev.filter((f) => !f.isSignature);
      if (existingImages.length >= 2) {
        notification.open({
          message: "Warning",
          description: "Maximum 2 images allowed. Please remove an existing image first.",
          placement: "topRight",
          icon: <FontAwesomeIcon style={{ color: "#f59e0b" }} icon={faInfoCircle} />,
          duration: 2,
        });
        return prev;
      }
      
      // Add new image
      const newFile = {
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: URL.createObjectURL(file),
        isSignature: false,
      };
      
      return [...prev, newFile];
    });

    // Reset file inputs
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const closeImageViewer = () => {
    setImageViewerOpen(false);
    setViewerImageSrc("");
    setViewerImageTitle("");
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleSubmit = async () => {
    const signatureFile = uploadedFiles.find((f) => f.isSignature);
    const imageFiles = uploadedFiles.filter((f) => !f.isSignature);
    
    if (!signatureFile) {
      notification.open({
        message: "Warning",
        description: "Please provide and save your signature before submitting.",
        placement: "topRight",
        icon: <FontAwesomeIcon style={{ color: "#f59e0b" }} icon={faInfoCircle} />,
        duration: 2,
      });
      return;
    }

    if (imageFiles.length === 0) {
      notification.open({
        message: "Warning",
        description: "Please upload at least one image before submitting.",
        placement: "topRight",
        icon: <FontAwesomeIcon style={{ color: "#f59e0b" }} icon={faInfoCircle} />,
        duration: 2,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Create FormData for file upload
      const image1 = await compressImageToJpeg(imageFiles[0].file);
      const image2 = await compressImageToJpeg(imageFiles[1].file);

      const formData = new FormData();
      formData.append("signature", signatureFile.file);
      if(imageFiles[0]){
        formData.append(`images`, image1);
      }
      if(imageFiles[1]){
        formData.append(`images2`, image2);
      }
      
      formData.append("id", voucherData?._id || "");
      const res = await payoutVoucher(formData);
      if(res?.status){
        notification.open({
          message: "Success",
          description: "Documents uploaded successfully!",
          placement: "topRight",
          icon: <FontAwesomeIcon style={{ color: "green" }} icon={faCheckCircle} />,
          duration: 2,
        });
      } else{
        notification.open({
          message: "Error",
          description: "Failed to upload documents. Please try again.",
          placement: "topRight",
          icon: <FontAwesomeIcon style={{ color: "red" }} icon={faInfoCircle} />,
          duration: 2,
        });
      }

      handleCancel();
    } catch (error) {
      notification.open({
        message: "Error",
        description: "Failed to upload documents. Please try again.",
        placement: "topRight",
        icon: <FontAwesomeIcon style={{ color: "red" }} icon={faInfoCircle} />,
        duration: 2,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadVoucher = () => {
    navigate('/voucher-pdf', {state : voucherData});
  };

  const handleWhatsAppShare = () => {
    if (!voucherData?.order_no) {
      alert('Voucher order no not available');
      return;
    }

    // Generate voucher URL
    const baseUrl = window.location.origin;
    const voucherUrl = `${baseUrl}/voucher/${voucherData.order_no}`;

    // Create pre-filled message
    const message = `*🎉 Winning Voucher Details*\n\n` +
      `Order ID: *${voucherData.order_no || 'N/A'}*\n` +
      `Game: ${voucherData.game_name || 'N/A'}\n` +
      `Winner Name: ${voucherData.payment_to || 'N/A'}\n` +
      `Seller Name: ${voucherData.seller_name || 'N/A'}\n` +
      `Amount: ${voucherData.total_amount || 'N/A'}\n` +
      `Draw Date: ${voucherData.draw_date ? moment(voucherData.draw_date).format('DD-MM-YYYY') : 'N/A'}\n` +
      `Area : ${voucherData.area || '--'}\n\n` +
      `View voucher: ${voucherUrl}`;

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp with pre-filled message
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <>
      <Dialog
        open={popUpOpen}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          className: "popup-container",
          style: {
            borderRadius: isMobile ? 0 : 20,
            padding: 0,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
            margin: isMobile ? 0 : isTablet ? "16px" : "32px",
            maxHeight: isMobile ? "100vh" : "90vh",
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
            maxHeight: isMobile ? "100vh" : "90vh",
            overflowY: "auto",
          }}
        >
          {/* Close Button */}
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            disabled={isSubmitting}
            sx={{
              position: "absolute",
              right: isMobile ? 8 : 12,
              top: isMobile ? 8 : 12,
              color: "#6b7280",
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              padding: isMobile ? "8px" : "12px",
              "&:hover": {
                // backgroundColor: "rgba(239, 15, 15, 0.94)",
                color: "#374151",
              },
              transition: "all 0.2s ease",
            }}
            size={isMobile ? "small" : "medium"}
          >
            <FontAwesomeIcon icon={faClose} style={{ fontSize: isMobile ? "40px" : "30px", color: "red" }} />
          </IconButton>

          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: isMobile ? 2 : 3,
            }}
          >
            <Box
              sx={{
                width: isMobile ? 56 : 64,
                height: isMobile ? 56 : 64,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #4f46e5 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                boxShadow: "0 8px 24px rgba(13, 148, 136, 0.35)",
              }}
            >
              <FontAwesomeIcon icon={faMoneyBillWave} style={{ fontSize: isMobile ? "24px" : "28px" }} />
            </Box>
          </Box>

          <Typography
            variant={isMobile ? "h6" : "h5"}
            component="div"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              marginBottom: 1,
              background: "linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #4f46e5 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontSize: isMobile ? "20px" : isTablet ? "22px" : "24px",
            }}
          >
            Payout Request
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b7280",
              textAlign: "center",
              marginBottom: isMobile ? 2 : 3,
              fontSize: isMobile ? "12px" : "14px",
              paddingX: isMobile ? 1 : 0,
            }}
          >
            Upload documents for voucher payout processing
          </Typography>

          {/* Voucher Details Section */}
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: isMobile ? "8px" : "12px",
              padding: isMobile ? "16px" : "20px",
              marginBottom: isMobile ? 2 : 3,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                marginBottom: isMobile ? 1.5 : 2,
                color: "#111827",
                fontSize: isMobile ? "16px" : isTablet ? "17px" : "18px",
              }}
            >
              Voucher Details
            </Typography>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Voucher No.</span>
              <span className="voucher-detail-value">{voucherData?.voucher_no || "-"}</span>
            </div>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Ticket ID</span>
              <span className="voucher-detail-value">{voucherData?.order_no || "-"}</span>
            </div>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Payment To</span>
              <span className="voucher-detail-value">{voucherData?.payment_to || "-"}</span>
            </div>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Seller Name</span>
              <span className="voucher-detail-value">{voucherData?.seller_name || "-"}</span>
            </div>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Seller Area</span>
              <span className="voucher-detail-value">{voucherData?.area || "-"}</span>
            </div>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Winning Amount</span>
              <span className="voucher-detail-value" style={{ color: "var(--agent-primary-dark)", fontWeight: 700 }}>
                QR {parseFloat(voucherData?.total_amount || 0).toFixed(2)}
              </span>
            </div>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Prepared By</span>
              <span className="voucher-detail-value">{voucherData?.prepared_by || "-"}</span>
            </div>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Purchase Date</span>
              <span className="voucher-detail-value">
                {voucherData?.purchase_date
                  ? moment(voucherData.purchase_date).format("DD-MM-YYYY")
                  : "-"}
              </span>
            </div>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Draw Date</span>
              <span className="voucher-detail-value">
                {voucherData?.draw_date ? moment(voucherData.draw_date).format("DD-MM-YYYY") : "-"}
              </span>
            </div>

            <div className="voucher-detail-row">
              <span className="voucher-detail-label">Payout Status</span>
              <span
                className="voucher-detail-value"
                dangerouslySetInnerHTML={{ __html: statusMessage(voucherData?.payout_status) }}
              ></span>
            </div>

            {voucherData?.payout_status === "Completed" && (
              <div className="voucher-detail-row">
                <span className="voucher-detail-label">Share</span>
                <span 
                  className="voucher-detail-value" 
                  style={{ cursor: "pointer", color: "#3b82f6", textDecoration: "underline" }}
                  onClick={handleWhatsAppShare}
                >
                  <FontAwesomeIcon icon={faShare} /> WhatsApp
                </span>
              </div>
            )}
            <div className="voucher-detail-row">
              <span className="voucher-detail-label">View Voucher</span>
              <span 
                className="voucher-detail-value" 
                style={{ cursor: "pointer", color: "#3b82f6", textDecoration: "underline" }}
                onClick={handleDownloadVoucher}
              >
                <FontAwesomeIcon icon={faEye} /> PDF
              </span>
            </div>

          </Box>
          {/* Image Upload Section */}
          {(!voucherData?.payout_status || voucherData?.payout_status === "" || voucherData?.payout_status === "Pending") && (
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: isMobile ? "8px" : "12px",
              padding: isMobile ? "16px" : "20px",
              marginBottom: isMobile ? 2 : 3,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            {uploadedFiles.filter((f) => !f.isSignature).length < 2 && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: isMobile ? 1.5 : 2,
                    color: "#111827",
                    fontSize: isMobile ? "16px" : isTablet ? "17px" : "18px",
                  }}
                >
                  Upload Images
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#6b7280",
                    marginBottom: isMobile ? 1.5 : 2,
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Upload up to 2 supporting documents or images (max 2)
                </Typography>

                {/* File Inputs */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                  disabled={isSubmitting}
                />
                
                {/* Camera Input - for mobile camera capture */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                  disabled={isSubmitting}
                />

                {/* Upload Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: isMobile ? 1 : 1.5,
                    flexDirection: isMobile ? "column" : "row",
                    marginBottom: uploadedFiles.filter((f) => !f.isSignature).length > 0 ? (isMobile ? 2 : 2.5) : 0,
                  }}
                >
                  {/* Gallery Button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting || uploadedFiles.filter((f) => !f.isSignature).length >= 2}
                    className="btn btn-light"
                    style={{
                      flex: isMobile ? "none" : 1,
                      width: isMobile ? "100%" : "auto",
                      borderRadius: isMobile ? "10px" : "12px",
                      padding: isMobile ? "12px 20px" : "10px 24px",
                      fontSize: isMobile ? "14px" : "15px",
                      fontWeight: 600,
                      borderColor: "#3b82f6",
                      color: "#3b82f6",
                      backgroundColor: "#eff6ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      cursor: (isSubmitting || uploadedFiles.filter((f) => !f.isSignature).length >= 2) ? "not-allowed" : "pointer",
                      opacity: (isSubmitting || uploadedFiles.filter((f) => !f.isSignature).length >= 2) ? 0.5 : 1,
                    }}
                  >
                    <FontAwesomeIcon icon={faUpload} style={{ fontSize: isMobile ? "13px" : "14px" }} />
                    {isMobile ? "Choose from Gallery" : "Choose Images"}
                  </button>

                  {/* Camera Button */}
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={isSubmitting || uploadedFiles.filter((f) => !f.isSignature).length >= 2}
                    className="btn btn-light"
                    style={{
                      flex: isMobile ? "none" : 1,
                      width: isMobile ? "100%" : "auto",
                      borderRadius: isMobile ? "10px" : "12px",
                      padding: isMobile ? "12px 20px" : "10px 24px",
                      fontSize: isMobile ? "14px" : "15px",
                      fontWeight: 600,
                      borderColor: "#10b981",
                      color: "#10b981",
                      backgroundColor: "#ecfdf5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      cursor: (isSubmitting || uploadedFiles.filter((f) => !f.isSignature).length >= 2) ? "not-allowed" : "pointer",
                      opacity: (isSubmitting || uploadedFiles.filter((f) => !f.isSignature).length >= 2) ? 0.5 : 1,
                    }}
                  >
                    <FontAwesomeIcon icon={faCamera} style={{ fontSize: isMobile ? "13px" : "14px" }} />
                    {isMobile ? "Capture Photo" : "Take Photo"}
                  </button>
                </Box>
              </>
            )}
            
            {/* Uploaded Images List */}
            {uploadedFiles.filter((f) => !f.isSignature).length > 0 && (
              <Box sx={{ marginTop: isMobile ? 2 : 2.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: isMobile ? 1 : 1.5,
                    fontSize: isMobile ? "13px" : "14px",
                  }}
                >
                  Uploaded Images ({uploadedFiles.filter((f) => !f.isSignature).length}/2)
                </Typography>

                {uploadedFiles
                  .filter((f) => !f.isSignature)
                  .map((fileObj) => (
                    <div key={fileObj.id} className="uploaded-file-item">
                      <Box 
                        sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          flex: 1, 
                          minWidth: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          if (fileObj.preview) {
                            setViewerImageSrc(fileObj.preview);
                            setViewerImageTitle(fileObj.name);
                            setImageViewerOpen(true);
                            setImageLoading(false);
                          }
                        }}
                      >
                        {fileObj.preview && (
                          <img
                            src={fileObj.preview}
                            alt={fileObj.name}
                            className="file-preview"
                            style={{
                              width: isMobile ? "50px" : "60px",
                              height: isMobile ? "50px" : "60px",
                              marginRight: isMobile ? "10px" : "12px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "4px",
                              objectFit: "cover",
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: "#111827",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: isMobile ? "13px" : "14px",
                            }}
                          >
                            {fileObj.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#6b7280",
                              fontSize: isMobile ? "11px" : "12px",
                            }}
                          >
                            {formatFileSize(fileObj.size)}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(fileObj.id);
                        }}
                        disabled={isSubmitting}
                        sx={{
                          color: "#ef4444",
                          padding: isMobile ? "8px" : "12px",
                          "&:hover": {
                            backgroundColor: "#fee2e2",
                          },
                        }}
                        size="small"
                      >
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: isMobile ? "14px" : "16px" }} />
                      </IconButton>
                    </div>
                  ))}
              </Box>
            )}
          </Box>
          )}
          
          {/* Signature Section */}
          {(!voucherData?.payout_status || voucherData?.payout_status === "" || voucherData?.payout_status === "Pending") && (

          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: isMobile ? "8px" : "12px",
              padding: isMobile ? "16px" : "20px",
              marginBottom: isMobile ? 2 : 3,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            {!uploadedFiles.some((f) => f.isSignature) && (<>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  marginBottom: isMobile ? 1.5 : 2,
                  color: "#111827",
                  fontSize: isMobile ? "16px" : isTablet ? "17px" : "18px",
                }}
              >
                Signature
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#6b7280",
                  marginBottom: isMobile ? 1.5 : 2,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Please sign below using your mouse or touch screen
              </Typography>

              {/* Signature Canvas */}
              <Box
                sx={{
                  border: "2px solid #e5e7eb",
                  borderRadius: isMobile ? "8px" : "12px",
                  backgroundColor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  marginBottom: isMobile ? 2 : 2.5,
                  touchAction: "none",
                }}
              >
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  style={{
                    width: "100%",
                    height: isMobile ? "200px" : isTablet ? "250px" : "300px",
                    cursor: "crosshair",
                    display: "block",
                  }}
                />
              </Box>
            </>)}

            {/* Signature Actions */}
            {!uploadedFiles.some((f) => f.isSignature) && (
              <Box
                sx={{
                  display: "flex",
                  gap: isMobile ? 1 : 1.5,
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                <button
                  type="button"
                  onClick={clearSignature}
                  disabled={!hasSignature || isSubmitting}
                  className="btn btn-light"
                  style={{
                    flex: isMobile ? "none" : 1,
                    width: isMobile ? "100%" : "auto",
                    borderRadius: isMobile ? "10px" : "12px",
                    padding: isMobile ? "12px 20px" : "10px 24px",
                    fontSize: isMobile ? "14px" : "15px",
                    fontWeight: 600,
                    borderColor: "#d1d5db",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    opacity: !hasSignature ? 0.5 : 1,
                    cursor: !hasSignature ? "not-allowed" : "pointer",
                  }}
                >
                  <FontAwesomeIcon icon={faEraser} style={{ fontSize: isMobile ? "13px" : "14px" }} />
                  Clear
                </button>
                <button
                  type="button"
                  onClick={saveSignature}
                  disabled={!hasSignature || isSubmitting}
                  className="btn btn-dark"
                  style={{
                    flex: isMobile ? "none" : 1,
                    width: isMobile ? "100%" : "auto",
                    borderRadius: isMobile ? "10px" : "12px",
                    padding: isMobile ? "12px 20px" : "10px 24px",
                    fontSize: isMobile ? "14px" : "15px",
                    fontWeight: 600,
                    background: !hasSignature
                      ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: !hasSignature
                      ? "none"
                      : "0 8px 20px rgba(102, 126, 234, 0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    opacity: !hasSignature ? 0.5 : 1,
                    cursor: !hasSignature ? "not-allowed" : "pointer",
                  }}
                >
                  <FontAwesomeIcon icon={faPen} style={{ fontSize: isMobile ? "13px" : "14px" }} />
                  Save Signature
                </button>
              </Box>
            )}

            {/* Saved Signature Preview */}
            {uploadedFiles.some((f) => f.isSignature) && (
              <Box sx={{ marginTop: isMobile ? 2 : 2.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: isMobile ? 1 : 1.5,
                    fontSize: isMobile ? "13px" : "14px",
                  }}
                >
                  Saved Signature
                </Typography>

                {uploadedFiles
                  .filter((f) => f.isSignature)
                  .map((fileObj) => (
                    <div key={fileObj.id} className="uploaded-file-item">
                      <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                        {fileObj.preview && (
                          <img
                            src={fileObj.preview}
                            alt={fileObj.name}
                            className="file-preview"
                            style={{
                              width: isMobile ? "50px" : "60px",
                              height: isMobile ? "50px" : "60px",
                              marginRight: isMobile ? "10px" : "12px",
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: "#111827",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: isMobile ? "13px" : "14px",
                            }}
                          >
                            {fileObj.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#6b7280",
                              fontSize: isMobile ? "11px" : "12px",
                            }}
                          >
                            {formatFileSize(fileObj.size)}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        onClick={() => {
                          handleRemoveFile(fileObj.id);
                          clearSignature();
                        }}
                        disabled={isSubmitting}
                        sx={{
                          color: "#ef4444",
                          padding: isMobile ? "8px" : "12px",
                          "&:hover": {
                            backgroundColor: "#fee2e2",
                          },
                        }}
                        size="small"
                      >
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: isMobile ? "14px" : "16px" }} />
                      </IconButton>
                    </div>
                  ))}
              </Box>
            )}
          </Box>
          )}
          

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: isMobile ? 1.5 : 2,
              justifyContent: "flex-end",
              flexDirection: isMobile ? "column" : "row",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !uploadedFiles.some((f) => f.isSignature)}
              className="btn btn-dark"
              style={{
                width: isMobile ? "100%" : "auto",
                minWidth: isMobile ? "auto" : "120px",
                borderRadius: isMobile ? "10px" : "12px",
                padding: isMobile ? "12px 20px" : "10px 24px",
                fontSize: isMobile ? "14px" : "15px",
                fontWeight: 600,
                background: isSubmitting
                  ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
                  : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                boxShadow: isSubmitting
                  ? "none"
                  : "0 8px 20px rgba(16, 185, 129, 0.4)",
                opacity: !uploadedFiles.some((f) => f.isSignature) ? 0.5 : 1,
                cursor: !uploadedFiles.some((f) => f.isSignature) ? "not-allowed" : "pointer",
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CircularProgress size={isMobile ? 14 : 16} sx={{ color: "#fff" }} />
                  <span>Submitting...</span>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: isMobile ? "13px" : "14px" }} />
                  <span>Submit</span>
                </Box>
              )}
            </button>
            
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="btn btn-light"
              style={{
                width: isMobile ? "100%" : "auto",
                minWidth: isMobile ? "auto" : "120px",
                borderRadius: isMobile ? "10px" : "12px",
                padding: isMobile ? "12px 20px" : "10px 24px",
                fontSize: isMobile ? "14px" : "15px",
                fontWeight: 600,
                borderColor: "#d1d5db",
                color: "#374151",
              }}
            >
              Cancel
            </button>
            
          </Box>
          
        </Box>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageViewerOpen}
        onClose={closeImageViewer}
        maxWidth={false}
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            // backgroundColor: "rgba(0, 0, 0, 0.95)",
            borderRadius: isMobile ? 0 : 8,
            padding: 0,
            overflow: "hidden",
            margin: isMobile ? 0 : "16px",
            maxHeight: isMobile ? "100vh" : "95vh",
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: isMobile ? "100vh" : "95vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "16px" : "24px",
          }}
        >
          {/* Close Button */}
          <IconButton
            aria-label="close"
            onClick={closeImageViewer}
            sx={{
              position: "absolute",
              right: isMobile ? 8 : 16,
              top: isMobile ? 8 : 16,
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              padding: isMobile ? "10px" : "12px",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
              zIndex: 10,
              transition: "all 0.2s ease",
            }}
            size={isMobile ? "small" : "medium"}
          >
            <FontAwesomeIcon icon={faCircleXmark} style={{ fontSize: isMobile ? "20px" : "24px" }} />
          </IconButton>

          {/* Image Title */}
          {viewerImageTitle && (
            <Typography
              variant="h6"
              sx={{
                position: "absolute",
                top: isMobile ? 16 : 24,
                left: "50%",
                transform: "translateX(-50%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: isMobile ? "16px" : "20px",
                textAlign: "center",
                zIndex: 10,
                // backgroundColor: "rgba(250, 247, 247, 0.95)",
                padding: "8px 16px",
                borderRadius: "8px",
              }}
            >
              {viewerImageTitle}
            </Typography>
          )}

          {/* Image Container */}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "auto",
              paddingTop: viewerImageTitle ? (isMobile ? "60px" : "70px") : 0,
              paddingBottom: isMobile ? "16px" : "24px",
            }}
          >
            {imageLoading && (
              <CircularProgress
                sx={{
                  color: "#fff",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            )}
            <img
              src={viewerImageSrc}
              alt={viewerImageTitle || "Image"}
              onLoad={handleImageLoad}
              // onError={handleImageError}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 8px 32px rgba(114, 114, 114, 0.3)",
                opacity: imageLoading ? 0 : 1,
                transition: "opacity 0.3s ease",
              }}
            />
          </Box>

          {/* Helper Text */}
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              bottom: isMobile ? 16 : 24,
              color: "rgba(255, 255, 255, 0.7)",
              fontSize: isMobile ? "11px" : "12px",
              textAlign: "center",
            }}
          >
            Click outside or press ESC to close
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default PayoutPopup;
